/**
 * Klien HTTP konsol portal.
 *
 * ⛔ Tenant TIDAK PERNAH ikut di URL sesudah login — ia ada di dalam token (R-AI-1). Satu-satunya
 * tempat tenant disebut adalah formulir login, dan di situ ia bagian dari kredensial.
 */
import { logout, session } from './session';

export interface ApiError extends Error {
  readonly detail?: { error_code: string; message: string; trace_id: string };
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const s = session();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(s === undefined ? {} : { authorization: `Bearer ${s.token}` }),
      ...init.headers,
    },
  });

  /*
   * ⚠️ 401 di tengah pemakaian berarti sesinya sudah tak berlaku ⇒ keluarkan, supaya konsol tak
   * menampilkan data lama sambil menolak tiap perubahan.
   *
   * ⛔ **HANYA bila permintaannya memang membawa sesi.** Permintaan LOGIN juga dijawab 401 saat
   * sandinya salah — dan memperlakukannya sebagai "sesi kedaluwarsa" membuat halaman memuat ulang
   * tepat sebelum pesan galatnya sempat terbaca. Layarnya lalu terlihat "tidak melakukan apa-apa",
   * dan yang salah ketik sandi akan mengulanginya sampai akunnya terkunci.
   */
  if (res.status === 401 && s !== undefined) {
    logout();
    globalThis.location?.reload();
  }

  const teks = await res.text();
  if (!res.ok) {
    let detail;
    try { detail = JSON.parse(teks) as ApiError['detail']; } catch { detail = undefined; }
    const e = new Error(detail?.message ?? `HTTP ${String(res.status)}`) as ApiError & {
      detail?: unknown;
    };
    (e as { detail?: unknown }).detail = detail;
    throw e;
  }
  return teks === '' ? (undefined as T) : (JSON.parse(teks) as T);
}

/**
 * Hasil gerbang kesiapan outlet.
 *
 * ⚠️ Tipe ini sengaja MINIM — layar hanya butuh kunci, lolos/tidak, dan penjelasannya. Menyalin
 * bentuk penuh `ReadinessResult` dari `pos-core` akan mengikat konsol pada isi perut mesinnya,
 * padahal yang dijanjikan endpoint cuma bentuk di kawat.
 */
export interface ReadinessCheck {
  readonly key: string;
  readonly ok: boolean;
  readonly detail: string;
}
export interface Readiness {
  readonly outletCode: string;
  readonly ready: boolean;
  readonly checks: readonly ReadinessCheck[];
  readonly blockers: readonly string[];
}

export interface Outlet {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  /** ⭐ WAJIB: satu tenant boleh punya beberapa brand, dan outlet selalu milik salah satunya. */
  readonly brand_id: string;
  readonly sale_mode: 'table_service' | 'quick_service';
  readonly timezone: string;
  readonly day_cutover_time: string;
  readonly address?: string | null;
  readonly npwp?: string | null;
  readonly price_group_id?: string | null;
  /** ⭐ Kapan portal terakhir menekan Send Data untuk outlet ini. `null` = belum pernah. */
  readonly catalog_published_at?: string | null;
  readonly is_active: boolean;
}

export interface Department {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly sort_order: number;
  readonly is_active: boolean;
  /** ⭐ Departemen milik BRAND sejak migrasi 0034. */
  readonly brand_id?: string;
}

export interface Category extends Department {
  readonly department_id: string;
}

export interface Brand {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly business_type: 'fnb' | 'retail' | 'service';
  readonly logo_url?: string | null;
  readonly is_active: boolean;
  /** ⭐ Ambang usia tiket dapur (detik) — milik BRAND, boleh ditimpa outlet (0043). */
  readonly kds_warn_seconds?: number;
  readonly kds_late_seconds?: number;
  /*
   * ⭐⭐ **Kebijakan brand** (0046) — bawaan yang berlaku di seluruh outlet brand ini.
   * Tiap outlet boleh menimpanya satu per satu; `NULL` di sisi outlet berarti "ikut brand".
   */
  readonly reprint_window_minutes?: number;
  readonly cash_variance_threshold?: string;
  readonly allow_reopen_after_cash_tender?: boolean;
  readonly drawer_open_review_threshold?: number;
  readonly guest_call_cooldown_seconds?: number;
  readonly guest_call_spam_threshold?: number;
  readonly guest_call_suspend_minutes?: number;
  readonly guest_call_requires_open_shift?: boolean;
  readonly print_zero_price_modifiers?: boolean;
  /** ⭐⭐ `0` = mati · `> 0` = boleh dibuang selama N menit sejak baris sampai dapur (0048). */
  readonly remove_sent_line_window_minutes?: number;
}

export interface PriceGroup {
  readonly id: string;
  readonly brand_id: string;
  readonly code: string;
  readonly name: string;
  readonly is_active: boolean;
}

/**
 * Metode bayar. ⛔ `tender_type` adalah **klasifikasi ekonomi**, bukan label — dan ia BEKU sesudah
 * dibuat: mengubahnya memindahkan transaksi kemarin ke kategori lain tanpa ada yang memutuskannya.
 */
export interface PaymentMethod {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly tender_type: string;
  /** ⭐ `null` = berlaku di SEMUA brand (0049). Bukan "belum diisi". */
  readonly brand_id?: string | null;
  readonly opens_drawer: boolean;
  readonly requires_reference: boolean;
  readonly settlement_provider?: string | null;
  /** ⚠️ Pecahan berskala 6 (`0.007000` = 0,7%), bukan persen. */
  readonly mdr_percent?: string | null;
  readonly is_active: boolean;
}

/** Langganan tenant — HANYA BACA. ⛔ Tanpa kolom uang: harga urusan penagihan, bukan buku besar. */
export interface Subscription {
  readonly tenantCode: string;
  readonly plan: { readonly code: string; readonly name: string } | null;
  readonly status: string | null;
  readonly validFrom: string | null;
  readonly validTo: string | null;
  /** ⭐ `limit: null` = TANPA BATAS, bukan nol. */
  readonly entitlements: readonly { readonly featureCode: string; readonly limit: number | null }[];
}

export interface TaxGroup {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  /**
   * ⭐⭐ **Kelas fiskal yang SENGAJA tanpa pajak** (0078) — rokok, voucher, barang titipan.
   * ⛔ Membedakannya dari daftar kosong karena LUPA: yang bertanda dilewati gerbang kesiapan
   * outlet, yang tak bertanda tetap ditahan. ⚠️ Opsional: portal versi lama tak mengirimnya.
   */
  readonly no_tax_intended?: boolean;
  readonly is_active: boolean;
}

export interface TaxCode {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly tax_kind: 'vat' | 'local_tax' | 'service_charge' | 'withholding';
  readonly rate: string;
  readonly sequence: number;
  readonly is_inclusive: boolean;
  /** `net_sales` · `service_charge` · `tax:<code>` — "dikenakan di atas APA" (§8). */
  readonly base_components: readonly string[];
  readonly valid_from: string;
  readonly valid_to: string | null;
}

/** Kapabilitas outlet — INI yang dibaca engine, bukan jenis usaha maupun presetnya. */
export interface OutletCapabilityRow {
  readonly id: string;
  readonly outlet_id: string;
  readonly capability: string;
}

/** Penugasan kode pajak ke satu outlet — PB1 pajak DAERAH, tarifnya ikut kota outletnya. */
export interface OutletTax {
  readonly id: string;
  readonly outlet_id: string;
  readonly tax_code_id: string;
  readonly valid_from: string;
  readonly valid_to: string | null;
}

/** Baris INKLUSI: keberadaannya berarti "grup ini KENA kode pajak itu". */
export interface TaxGroupLink {
  readonly id: string;
  readonly tax_group_id: string;
  readonly tax_code_id: string;
}

/** Baris PEMBATAS: tanpa satu pun baris, kode pajak berlaku di SEMUA kanal. */
export interface TaxCodeChannel {
  readonly id: string;
  readonly tax_code_id: string;
  readonly channel: string;
}

/** Satu baris jejak perubahan master (`master_event`). ⛔ Append-only — tak pernah di-UPDATE. */
export interface MasterEvent {
  readonly id: string;
  readonly action: string;
  readonly actor_kind: string;
  readonly actor_code_snapshot: string | null;
  readonly actor_name_snapshot: string | null;
  readonly before_json: Record<string, unknown> | null;
  readonly after_json: Record<string, unknown> | null;
  readonly created_at: string;
}

export interface Promo {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly kind: 'percent' | 'amount';
  readonly scope: 'order' | 'line';
  readonly percent_rate: string | null;
  readonly amount: string | null;
  readonly max_discount: string | null;
  readonly min_subtotal: string | null;
  /**
   * ⭐⭐ Butuh persetujuan penyelia ber-izin `promo:approve` — **kontrol yang MEMAKSA** sejak
   * 2026-08-06. Sebelum itu kolomnya dibaca mesin lalu diabaikan.
   */
  readonly requires_approval: boolean;
  readonly is_active: boolean;
  /*
   * ⭐ Masa berlaku — sudah DITEGAKKAN mesin (`pos-core/promo.ts`) jauh sebelum layarnya ada.
   * ⚠️ `valid_days`: 1 = Senin … 7 = Minggu, penomoran yang SAMA dengan mesinnya (bukan JS).
   */
  readonly valid_from: string | null;
  readonly valid_to: string | null;
  readonly valid_time_from: string | null;
  readonly valid_time_to: string | null;
  readonly valid_days: readonly number[] | null;
}

/**
 * Klien M2M milik tenant. ⛔ Tak pernah memuat kunci — yang publik pun tidak: layar tak punya
 * alasan melihatnya, dan yang tak dikirim tak bisa bocor lewat tangkapan layar.
 */
export interface ConsoleUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: string;
  readonly isActive: boolean;
  readonly lastLoginAt: string | null;
  /** ⭐ Terkunci karena salah sandi berkali-kali — pemilik perlu MELIHATnya untuk menolong. */
  readonly locked: boolean;
  readonly staffCode: string | null;
}

export interface IntegrationClient {
  readonly id: string;
  readonly clientId: string;
  /** ⭐ Agen AI — STRUKTURAL (`m2m_client.is_agent`), bukan tebakan dari nama. */
  readonly isAgent: boolean;
  readonly status: string;
  readonly scopes: readonly string[];
  readonly createdAt: string;
}

/** Baris PENGECUALIAN: keberadaannya berarti "TIDAK aktif di outlet ini". */
export interface PromoBlock {
  readonly id: string;
  readonly outlet_id: string;
  readonly promo_id: string;
}

/** Baris PENGECUALIAN: keberadaannya berarti "TIDAK tersedia di outlet ini". */
export interface MenuBlock {
  readonly id: string;
  readonly outlet_id: string;
  readonly department_id: string | null;
  readonly category_id: string | null;
  readonly product_id: string | null;
  readonly reason_note: string | null;
}

export interface Product {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly category_id: string;
  readonly product_kind: string;
  /** ⭐ Kelas fiskal — WAJIB sejak migrasi 0036. */
  readonly tax_group_id?: string;
  readonly is_active: boolean;
  readonly receipt_name?: string | null;
  readonly description?: string | null;
  readonly sort_order?: number;
  /** ⛔ Kontrol yang MEMAKSA — bukan catatan. Lihat `schema.ts`. */
  readonly discount_allowed?: boolean;
  readonly zero_price_allowed?: boolean;
  readonly returnable?: boolean;
  /** ⚠️ `null` = selalu tersedia. Bukan "00:00–23:59". */
  readonly available_from?: string | null;
  readonly available_to?: string | null;
  readonly available_time_from?: string | null;
  readonly available_time_to?: string | null;
  /** ISO 1=Senin … 7=Minggu. */
  readonly available_days?: number[] | null;
}

export interface Barcode {
  readonly id: string;
  readonly product_id: string;
  readonly barcode: string;
  readonly is_primary: boolean;
}

export interface Price {
  readonly id: string;
  readonly product_id: string;
  readonly channel: string;
  readonly outlet_id: string | null;
  /** ⛔ Terisi hanya bila `outlet_id` kosong — satu sasaran per baris. */
  readonly price_group_id: string | null;
  readonly price: string;
  readonly valid_from: string;
  readonly valid_to: string | null;
}

export interface PrintStation {
  readonly id: string;
  readonly outlet_id: string;
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  readonly is_default: boolean;
  readonly sort_order: number;
  readonly is_active: boolean;
}

export interface ProductRoute {
  readonly id: string;
  readonly product_id: string;
  readonly print_station_id: string;
}

/* ═══════════════════════ laporan (baca-saja) ══════════════════════════════ */

export interface DailySalesRow {
  readonly businessDate: string;
  readonly outletCode: string;
  readonly outletName: string;
  readonly bills: number;
  readonly guests: number;
  readonly subtotalGross: string;
  readonly discountTotal: string;
  readonly serviceChargeTotal: string;
  readonly taxTotal: string;
  readonly grandTotal: string;
  readonly tenderTotal: string;
  /** ⛔ `false` = Σ tender ≠ Σ penjualan. Bukan kosmetik: itu berarti datanya rusak. */
  readonly balanced: boolean;
}
export interface DailyTenderRow {
  readonly businessDate: string;
  readonly outletCode: string;
  readonly tenderType: string;
  readonly amount: string;
  readonly count: number;
  /** Uang yang DISERAHKAN tamu & kembaliannya — kolom sendiri, tak pernah dicampur ke `amount`. */
  readonly tenderedAmount: string;
  readonly changeGiven: string;
}
export interface DailySales {
  readonly rows: readonly DailySalesRow[];
  readonly tenders: readonly DailyTenderRow[];
}
export interface TaxRecapRow {
  readonly outletCode: string;
  readonly outletName: string;
  /** ⭐ KUNCInya kode, bukan nama: nama boleh diganti tenant di tengah periode. */
  readonly taxCode: string;
  readonly name: string;
  readonly kind: string;
  readonly rate: string;
  readonly isInclusive: boolean;
  readonly taxableBase: string;
  readonly taxAmount: string;
  readonly lines: number;
}
export interface TaxRecap {
  readonly rows: readonly TaxRecapRow[];
  readonly sales: {
    readonly bills: number; readonly grossPaid: string;
    readonly discountTotal: string; readonly grandTotal: string;
  };
  /** ⛔ `balanced:false` ⇒ rekap ≠ rollup pesanan. Jangan salin angkanya sebelum sebabnya ketemu. */
  readonly reconciliation: {
    readonly recapTotal: string; readonly orderRollupTotal: string; readonly balanced: boolean;
  };
}
/**
 * ⭐ Satu BARIS pesanan yang harganya menyimpang dari master — bukan satu bill.
 * Satu bill bisa menyumbang beberapa baris; menggabungkannya menyembunyikan produk mana.
 */
export interface PriceDriftRow {
  readonly outletCode: string;
  readonly outletName: string;
  readonly orderNo: string;
  readonly businessDate: string;
  readonly deviceCode: string | null;
  readonly catalogVersion: string | null;
  readonly productCode: string;
  readonly qty: string;
  readonly charged: string;
  readonly master: string;
  readonly delta: string;
  readonly at: string;
}

export interface ItemSalesRow {
  readonly outletCode: string;
  readonly outletName: string;
  readonly departmentCode: string | null;
  readonly departmentName: string | null;
  readonly categoryCode: string | null;
  readonly categoryName: string | null;
  readonly productCode: string;
  readonly productName: string;
  readonly qtySold: string;
  readonly grossSold: string;
  readonly discountTotal: string;
  readonly netSold: string;
  readonly qtyRefunded: string;
  readonly netRefunded: string;
  readonly netAfterRefund: string;
  /** Porsi terhadap Σ bersih, 0..1 — dihitung SERVER, tak dibagi di layar. */
  readonly share: string;
}

export interface ShiftRow {
  readonly businessDate: string;
  readonly outletCode: string;
  readonly shiftNo: string;
  readonly status: string;
  readonly openedByName: string | null;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly openingFloat: string;
  readonly expectedCash: string | null;
  readonly countedCash: string | null;
  readonly variance: string | null;
  readonly varianceReasonCode: string | null;
  readonly varianceReasonNote: string | null;
}

function qs(f: Record<string, string | undefined>): string {
  const p = Object.entries(f).filter(([, v]) => v !== undefined && v !== '');
  return p.length === 0 ? '' : `?${p.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')}`;
}

export interface KitchenTimingRow {
  readonly outletCode: string;
  readonly stationCode: string;
  readonly stationName: string;
  readonly tickets: number;
  readonly avgSeconds: number;
  readonly p50Seconds: number;
  readonly p90Seconds: number;
  readonly lateCount: number;
  readonly unfinished: number;
}

export interface NotificationRow {
  readonly id: string;
  readonly outletCode: string;
  readonly outletName: string;
  readonly kind: string;
  readonly title: string;
  readonly body: string;
  readonly businessDate: string | null;
  readonly createdAt: string;
  readonly data: Record<string, unknown>;
  readonly sent: number;
  readonly pending: number;
  readonly failed: number;
}

export interface NotificationTarget {
  readonly id: string;
  readonly outletCode: string;
  readonly channel: string;
  readonly address: string;
  readonly label: string | null;
  readonly isActive: boolean;
  readonly kinds: readonly string[];
  readonly failed: number;
  readonly lastError: string | null;
}

export interface Material {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly uom: string;
  readonly isActive: boolean;
}

export interface OnHandRow {
  readonly kind: 'product' | 'material';
  readonly code: string;
  readonly name: string;
  readonly uom: string | null;
  readonly qtyOnHand: string;
  readonly theoreticalSince: string;
}

export interface MovementRow {
  readonly id: string;
  readonly kind: string;
  readonly code: string;
  readonly name: string;
  readonly movementType: string;
  readonly qty: string;
  readonly isTheoretical: boolean;
  readonly businessDate: string;
  readonly reasonCode: string | null;
  readonly note: string | null;
  readonly actorName: string | null;
  readonly orderNo: string | null;
}

export interface RecipeRow {
  readonly product_code: string;
  readonly product_name: string;
  readonly yield_qty: string;
  readonly is_active: boolean;
  readonly lines: readonly {
    materialCode: string; materialName: string; uom: string; qty: string;
  }[];
}

export interface Coverage {
  readonly coveredPercent: string;
  readonly coveredValue: string;
  readonly totalValue: string;
  readonly topUncovered: readonly {
    productCode: string; productName: string; value: string; share: string;
  }[];
}

export const api = {
  loginPassword: (body: { tenant: string; email: string; password: string }) =>
    req<{
      access_token: string; expires_in: number; scope: string;
      role: string; display_name: string; tenant: string;
    }>('/auth/console/login', { method: 'POST', body: JSON.stringify(body) }),

  loginOutletPin: (body: { tenant: string; outletCode: string; staffCode: string; pin: string }) =>
    req<{
      access_token: string; expires_in: number; scope: string;
      role: string; display_name: string; tenant: string; outlet_code: string;
    }>('/auth/console/pin', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * ⭐⭐ **Laporan harian** — dikelompokkan menurut TANGGAL USAHA, dihitung SERVER.
   *
   * ⛔ Layar tak pernah menjumlahkan ulang dari transaksi mentah. Laporan yang dirakit di dua
   * tempat akan menyimpang, dan yang menyimpang di sini adalah angka yang dipakai orang menutup
   * laci dan membayar pajak.
   */
  dailySales: (from: string, to: string, outletCode?: string) =>
    req<DailySales>(`/reports/daily-sales${qs({ from, to, outletCode })}`),
  /**
   * ⭐⭐⭐ **Penjualan per ITEM** — *"menu mana yang laku?"*.
   *
   * ⚠️ Membawa kotor, refund, DAN bersih berdampingan: item yang laku keras tapi banyak
   * dikembalikan adalah sinyal paling berharga di laporan ini, dan ia hilang kalau cuma yang
   * bersih ditampilkan.
   */
  itemSales: (from: string, to: string, outletCode?: string) =>
    req<{ rows: readonly ItemSalesRow[] }>(`/reports/item-sales${qs({ from, to, outletCode })}`),
  /** ⭐ Setelan tingkat tenant — hari ini satu angka: batas jualan luring (keputusan #4). */
  tenantSettings: () => req<{ offlineMaxDays: number }>('/master/tenant-settings'),
  setTenantSettings: (offlineMaxDays: number) =>
    req<{ offlineMaxDays: number }>('/master/tenant-settings', {
      method: 'PATCH', body: JSON.stringify({ offlineMaxDays }),
    }),

  priceDrift: (from: string, to: string, outletCode?: string) =>
    req<{ rows: readonly PriceDriftRow[] }>(`/reports/price-drift${qs({ from, to, outletCode })}`),
  /**
   * ⭐⭐⭐ **Waktu masak dapur** — SLA yang akhirnya diukur. ⛔ Ambangnya disimpan sejak 0043 dan
   * layar dapur mewarnai tiket yang lewat batas, tapi tak satu pun laporan pernah memeriksanya.
   */
  kitchenTiming: (from: string, to: string, outletCode?: string) =>
    req<{ rows: readonly KitchenTimingRow[] }>(
      `/reports/kitchen-timing${qs({ from, to, outletCode })}`,
    ),
  notifications: (outletCode?: string) =>
    req<{
      rows: readonly NotificationRow[]; unread: number; availableChannels: readonly string[];
    }>(`/notifications${qs({ outletCode })}`),
  notificationsSeen: () => req<{ ok: boolean }>('/notifications/seen', { method: 'POST' }),
  notificationTargets: (outletCode?: string) =>
    req<{ rows: readonly NotificationTarget[]; availableChannels: readonly string[] }>(
      `/notifications/targets${qs({ outletCode })}`,
    ),
  saveNotificationTarget: (
    body: {
      outletCode: string; channel: string; address: string; label?: string | null;
      isActive?: boolean; kinds: readonly string[];
    },
    id?: string,
  ) =>
    req<{ id: string }>(
      id === undefined ? '/notifications/targets' : `/notifications/targets/${id}`,
      { method: id === undefined ? 'POST' : 'PUT', body: JSON.stringify(body) },
    ),
  deleteNotificationTarget: (id: string) =>
    req<{ ok: boolean }>(`/notifications/targets/${id}`, { method: 'DELETE' }),
  testNotificationTarget: (id: string) =>
    req<{ sent: number }>(`/notifications/targets/${id}/test`, { method: 'POST' }),

  stockOnHand: (outletCode: string) =>
    req<{ rows: readonly OnHandRow[] }>(`/stock/on-hand${qs({ outletCode })}`),
  stockMovements: (outletCode: string, from: string, to: string) =>
    req<{ rows: readonly MovementRow[] }>(`/stock/movements${qs({ outletCode, from, to })}`),
  stockReceive: (body: {
    outletCode: string; target: { kind: string; code: string }; qty: string;
    businessDate: string; note?: string | null;
  }) => req<{ movementId: string; onHand: string }>('/stock/receive',
    { method: 'POST', body: JSON.stringify(body) }),
  stockWaste: (body: {
    outletCode: string; target: { kind: string; code: string }; qty: string;
    businessDate: string; reasonCode: string; note?: string | null;
  }) => req<{ movementId: string; onHand: string }>('/stock/waste',
    { method: 'POST', body: JSON.stringify(body) }),
  stockOpname: (body: {
    outletCode: string; businessDate: string; note?: string | null;
    counts: readonly { target: { kind: string; code: string }; countedQty: string }[];
  }) => req<{ adjusted: number; unchanged: number; totalVariance: string }>('/stock/opname',
    { method: 'POST', body: JSON.stringify(body) }),
  recipes: (productCode?: string) =>
    req<{ rows: readonly RecipeRow[] }>(`/recipes${qs({ productCode })}`),
  saveRecipe: (body: {
    productCode: string; yieldQty: string;
    lines: readonly { materialCode: string; qty: string }[];
  }) => req<{ productCode: string; lines: number; deleted: boolean }>('/recipes',
    { method: 'POST', body: JSON.stringify(body) }),
  recipeCoverage: (from: string, to: string, outletCode?: string) =>
    req<Coverage>(`/reports/recipe-coverage${qs({ from, to, outletCode })}`),
  materials: (includeInactive = false) =>
    req<readonly Material[]>(
      `/master/materials${qs({ includeInactive: includeInactive ? 'true' : undefined })}`,
    ),
  saveMaterial: (body: { code?: string; name: string; uom: string }, id?: string) =>
    req<Material>(id === undefined ? '/master/materials' : `/master/materials/${id}`,
      { method: id === undefined ? 'POST' : 'PATCH', body: JSON.stringify(body) }),

  taxRecap: (from: string, to: string, outletCode?: string) =>
    req<TaxRecap>(`/reports/tax-recap${qs({ from, to, outletCode })}`),
  shiftReport: (from: string, to: string, outletCode?: string) =>
    req<{ rows: readonly ShiftRow[] }>(`/reports/shifts${qs({ from, to, outletCode })}`),

  /** ⛔ Soal `includeInactive`: lihat catatan di `departments`. */
  outlets: (brandId?: string, includeInactive = false) =>
    req<readonly Outlet[]>(
      `/master/outlets${qs({
        brand_id: brandId,
        includeInactive: includeInactive ? 'true' : undefined,
      })}`,
    ),
  /**
   * ⭐⭐ Gerbang kesiapan outlet — **satu-satunya sumber** untuk "apa yang masih kurang".
   *
   * ⛔ Layar TIDAK menghitung ulang. Kalau ia punya rumusnya sendiri, ia akan menyimpang dari
   * gerbang yang sesungguhnya menolak (`openShift`) — dan yang menyimpang justru terbaca sebagai
   * "layar bilang siap, kasir tetap ditolak", pesan paling membingungkan yang bisa dihasilkan.
   */
  readiness: (outletCode: string) =>
    req<Readiness>(`/outlets/${encodeURIComponent(outletCode)}/readiness`),

  createOutlet: (b: Record<string, unknown>) =>
    req<Outlet>('/master/outlets', { method: 'POST', body: JSON.stringify(b) }),

  /**
   * Setelan per outlet. ⚠️ Barisnya bisa BELUM ADA — outlet yang tak pernah disetel apa pun tak
   * punya barisnya, dan itu keadaan yang sah (semuanya mengikuti bawaan/brand).
   */
  outletSettings: () =>
    req<readonly Record<string, unknown>[]>('/master/outlet-settings'),
  createOutletSettings: (body: Record<string, unknown>) =>
    req<{ id: string }>('/master/outlet-settings', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  brands: (includeInactive = false) =>
    req<readonly Brand[]>(
      `/master/brands${qs({ includeInactive: includeInactive ? 'true' : undefined })}`,
    ),
  createBrand: (b: {
    code: string; name: string; businessType: string; logoUrl?: string;
    /** ⭐ Ambang KDS ikut sejak awal — brand baru tanpa ambang berarti outletnya memakai bawaan
     *  yang tak pernah diputuskan siapa pun. */
    kdsWarnSeconds?: number; kdsLateSeconds?: number;
  }) =>
    req<Brand>('/master/brands', { method: 'POST', body: JSON.stringify(b) }),

  /**
   * ⚠️ Disaring per brand: tiap brand punya struktur menunya sendiri.
   *
   * ⛔ **`includeInactive` wajib disebut di layar yang MENGELOLA master.** Bawaan server adalah
   * aktif-saja — benar untuk layar yang memakai master (rute, produk, kasir), tapi bencana untuk
   * layar yang mengurusnya: yang dinonaktifkan akan lenyap dari daftar, dan sesudah itu tak ada
   * lagi jalan menyalakannya kembali kecuali lewat SQL.
   */
  departments: (brandId?: string, includeInactive = false) =>
    req<readonly Department[]>(
      `/master/departments${qs({
        brand_id: brandId,
        includeInactive: includeInactive ? 'true' : undefined,
      })}`,
    ),
  createDepartment: (b: { brandId: string; code: string; name: string; sortOrder?: number }) =>
    req<Department>('/master/departments', { method: 'POST', body: JSON.stringify(b) }),

  /** Blokir menu per outlet — baris = "TIDAK di outlet ini". Cascade dihitung server saat baca. */
  blocks: (outletId: string) =>
    req<readonly MenuBlock[]>(`/master/outlet-menu-blocks${qs({ outlet_id: outletId })}`),
  block: (b: {
    outletId: string; departmentId?: string; categoryId?: string; productId?: string;
    reasonNote?: string;
  }) => req<MenuBlock>('/master/outlet-menu-blocks', { method: 'POST', body: JSON.stringify(b) }),
  unblock: (id: string) =>
    req<{ deleted: boolean }>(`/master/outlet-menu-blocks/${id}`, { method: 'DELETE' }),

  /** ⛔ Soal `includeInactive`: lihat catatan di `departments`. */
  categories: (departmentId?: string, includeInactive = false) =>
    req<readonly Category[]>(
      `/master/categories${qs({
        department_id: departmentId,
        includeInactive: includeInactive ? 'true' : undefined,
      })}`,
    ),
  createCategory: (b: { code: string; name: string; departmentId: string; sortOrder?: number }) =>
    req<Category>('/master/categories', { method: 'POST', body: JSON.stringify(b) }),

  /** ⛔ Soal `includeInactive`: lihat catatan di `departments`. */
  products: (categoryId?: string, includeInactive = false) =>
    req<readonly Product[]>(
      `/master/products${qs({
        category_id: categoryId,
        includeInactive: includeInactive ? 'true' : undefined,
      })}`,
    ),
  createProduct: (b: {
    code: string; name: string; categoryId: string; taxGroupId: string;
    /** ⛔ BEKU sesudah lahir — lihat `FROZEN_COLUMNS.product`. Karena itu ia ada di form BUAT. */
    productKind?: 'simple' | 'combo';
  }) =>
    req<Product>('/master/products', { method: 'POST', body: JSON.stringify(b) }),

  /** ⭐ Menyunting produk — termasuk flag kontrol & ketersediaan berjadwal. */
  updateProduct: (id: string, body: Record<string, unknown>) =>
    req<Product>(`/master/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  barcodes: (productId: string) =>
    req<readonly Barcode[]>(`/master/product-barcodes${qs({ product_id: productId })}`),
  /* ⛔ TABEL, bukan `Bar Code 1/2/3` ala VTEC: barcode keempat tak boleh mustahil, dan pencarian
     tak boleh harus memeriksa tiga kolom sekaligus. */
  createBarcode: (b: { productId: string; barcode: string; isPrimary?: boolean }) =>
    req<Barcode>('/master/product-barcodes', { method: 'POST', body: JSON.stringify(b) }),
  deleteBarcode: (id: string) =>
    req<{ deleted: boolean }>(`/master/product-barcodes/${id}`, { method: 'DELETE' }),

  rename: (path: string, id: string, name: string) =>
    req<{ id: string }>(`/master/${path}/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),

  /**
   * Menyunting master apa pun lewat registry.
   *
   * ⛔ **Kode tak pernah ikut** — server memang tak menerimanya (`update` di `master.registry.ts`
   * tak punya `code`). Kode hidup di struk lama, laporan, dan berkas impor; mengubahnya membuat
   * data kemarin menunjuk ke sesuatu yang kini bernama lain.
   */
  updateMaster: (path: string, id: string, body: Record<string, unknown>) =>
    req<{ id: string }>(`/master/${path}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /**
   * ⭐⭐ **Kode pemasangan perangkat** — sekali pakai, hidup 15 menit.
   *
   * ⛔ Tak ada endpoint "lihat kode": server menyimpan hash-nya, bukan nilainya. Memanggil ini lagi
   * menerbitkan kode BARU dan mematikan yang lama.
   */
  pairDevice: (deviceCode: string) =>
    req<{ code: string; expiresAt: string }>(
      `/devices/${encodeURIComponent(deviceCode)}/pairing-code`,
      { method: 'POST' },
    ),

  /**
   * ⭐⭐ **Riwayat perubahan satu baris master** — jawaban atas *"siapa mengubah harga ini, kapan?"*.
   *
   * ⛔ `master_event` sudah append-only sejak awal (P5) dan tak pernah bisa dilihat siapa pun dari
   * portal. Jejak yang tak bisa dibaca hanya membebani disk: ia tak pernah menjawab pertanyaan yang
   * membuatnya ditulis.
   */
  history: (path: string, id: string) =>
    req<readonly MasterEvent[]>(`/master/${path}/${encodeURIComponent(id)}/history`),

  /* ─────────────────── Klien integrasi tenant (M2M) ─────────────────── */

  /**
   * ⭐⭐ **Send Data** — menandai ada master baru untuk outlet/brand.
   *
   * ⛔ Ini TIDAK mengubah apa pun di kasir; ia menyalakan tombol SYNC di layar mereka. Yang
   * menarik tetap kasir — harga yang berganti sendiri di tengah antrean merampas kendali dari
   * orang yang sedang berdiri di depan tamu.
   */
  publishCatalog: (body: { brandId?: string; outletCodes?: readonly string[] }) =>
    req<{ outletCodes: readonly string[]; publishedAt: string }>('/catalog/publish', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /* ── pengguna konsol (Imron, 2026-08-07) ─────────────────────────────── */
  /** ⛔ Hanya `owner` yang dilayani server (`AUTH-403-NOT_OWNER`); layar cuma menyembunyikan tabnya. */
  consoleUsers: () => req<readonly ConsoleUser[]>('/console/users'),
  createConsoleUser: (body: {
    email: string; password: string; displayName: string; role: string;
  }) => req<{ id: string; email: string; role: string }>('/console/users', {
    method: 'POST', body: JSON.stringify(body),
  }),
  setConsoleUserStatus: (id: string, isActive: boolean) =>
    req<{ id: string; isActive: boolean }>(`/console/users/${id}/status`, {
      method: 'PATCH', body: JSON.stringify({ isActive }),
    }),
  setConsoleUserRole: (id: string, role: string) =>
    req<{ id: string; role: string }>(`/console/users/${id}/role`, {
      method: 'PATCH', body: JSON.stringify({ role }),
    }),
  /** ⭐ Setel ulang sandi orang lain — ia MEMBUKA KUNCI sekalian. */
  resetConsoleUserPassword: (id: string, password: string) =>
    req<{ id: string; unlocked: boolean }>(`/console/users/${id}/password`, {
      method: 'POST', body: JSON.stringify({ password }),
    }),
  /** ⭐⭐ Ganti sandi SENDIRI — sandi lama WAJIB; itu yang menjaganya, bukan peran. */
  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    req<{ id: string }>('/console/me/password', {
      method: 'POST', body: JSON.stringify({ currentPassword, newPassword }),
    }),

  integrationClients: () => req<readonly IntegrationClient[]>('/integrations/clients'),

  /**
   * ⭐⭐ Membuat klien. **Kunci privat ada di respons ini dan tidak di mana pun lagi** — server
   * menyimpan yang publik saja. ⛔ Jangan menyimpannya ke `localStorage` "supaya bisa dilihat
   * lagi": kunci yang menetap di peramban adalah kunci yang ikut hilang bersama peramban itu, dan
   * ikut terbawa siapa pun yang membuka DevTools di mesin yang sama.
   */
  createIntegrationClient: (body: { name: string; scopes: readonly string[]; isAgent: boolean }) =>
    req<{ clientId: string; privateJwk: unknown; scopes: readonly string[] }>(
      '/integrations/clients',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  setIntegrationStatus: (id: string, active: boolean) =>
    req<{ status: string }>(`/integrations/clients/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),

  setStatus: (path: string, id: string, isActive: boolean) =>
    req<{ id: string }>(`/master/${path}/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    }),

  prices: (productId: string) =>
    req<readonly Price[]>(`/master/product-prices${qs({ product_id: productId })}`),

  /**
   * ⭐⭐ **Harga BARU, bukan harga yang ditimpa.**
   *
   * Server memang hanya membolehkan `validTo` diubah — menimpa `price` akan menghapus jawaban atas
   * "berapa harganya bulan lalu?", dan pertanyaan itu selalu datang saat ada yang membantah struk.
   */
  createPrice: (b: {
    productId: string;
    channel: string;
    price: string;
    validFrom: string;
    /*
     * ⛔ SATU sasaran per baris — outlet, ATAU grup, ATAU umum (tak satu pun terisi). Ditegakkan
     * CHECK `product_price_one_target` di DB: baris bersasaran ganda tak punya jawaban tunggal
     * atas "ini harga untuk siapa?", dan yang menjawabnya kemudian adalah urutan `ORDER BY` yang
     * kebetulan dipakai hari itu.
     */
    outletId?: string | null;
    priceGroupId?: string | null;
  }) => req<Price>('/master/product-prices', { method: 'POST', body: JSON.stringify(b) }),

  /**
   * ⭐⭐ Seluruh harga pada SATU KANAL — arah baca yang terbalik dari `prices(productId)`.
   *
   * Laci produk menjawab *"berapa harga produk ini, di kanal apa, sejak kapan"* (satu produk,
   * banyak waktu). Halaman Harga menjawab sebaliknya: *satu kanal, banyak produk, beberapa
   * sasaran sekaligus* — dan menjawabnya lewat laci produk berarti membuka 300 laci satu per satu.
   *
   * ⛔ Disaring per KANAL, bukan diambil semuanya sekaligus: batas atas daftar master adalah 1000
   * baris, dan 300 produk × 6 kanal × beberapa sasaran melampauinya jauh. Daftar yang diam-diam
   * terpotong menghasilkan layar yang bilang "belum ada harga" untuk produk yang sebenarnya
   * berharga — dan yang mengisinya akan menimpanya.
   */
  pricesByChannel: (channel: string) =>
    req<readonly Price[]>(`/master/product-prices${qs({ channel, limit: '1000' })}`),

  /**
   * ⭐⭐ **Membuang baris harga yang lahir HARI INI** — bukan menutupnya.
   *
   * ⛔ CHECK `product_price_range` menuntut `valid_to > valid_from`. Menutup baris yang `valid_from`
   * -nya hari ini dengan `valid_to` hari ini melanggar itu, dan penolakannya muncul sebagai
   * `MASTER-400-INVALID` yang menyebut "melanggar aturan kolom" — kalimat yang tak seorang pun bisa
   * hubungkan dengan "harga ini baru saya isi tadi".
   *
   * ⚠️ Membuangnya AMAN untuk jejak: baris yang tak pernah melewati satu hari pun tak menyimpan
   * sejarah yang bisa ditanyakan, dan penjualan yang terlanjur terjadi membawa harganya SENDIRI di
   * `order_line.unit_price_gross`. Yang dibuang master-nya, bukan bukti transaksinya.
   */
  deletePrice: (id: string) =>
    req<{ purged: boolean }>(`/master/product-prices/${id}`, { method: 'DELETE' }),

  /** Menutup baris harga lama — inilah "mengubah harga" versi yang jujur. */
  closePrice: (id: string, validTo: string) =>
    req<{ id: string }>(`/master/product-prices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ validTo }),
    }),

  /**
   * ⭐⭐ **Stasiun cetak/KDS — milik OUTLET.**
   * Kasus Penang Bistro: "Roti Canai" keluar di KDS ASIA (PB-KBS), printer PRT_DESSERT (PB-PKB),
   * KDS WESTERN (PB-MPP). Tujuan ditentukan jarak fisik meja penyiapan, bukan jenis makanannya.
   */
  stations: (outletId?: string, includeInactive = false) =>
    req<readonly PrintStation[]>(
      `/master/print-stations${qs({
        outlet_id: outletId,
        includeInactive: includeInactive ? 'true' : undefined,
      })}`,
    ),
  createStation: (b: {
    outletId: string; code: string; name: string; kind: string; isDefault?: boolean;
    sortOrder?: number;
  }) => req<PrintStation>('/master/print-stations', { method: 'POST', body: JSON.stringify(b) }),

  routes: (productId: string) =>
    req<readonly ProductRoute[]>(`/master/product-routes${qs({ product_id: productId })}`),
  createRoute: (b: { productId: string; printStationId: string }) =>
    req<ProductRoute>('/master/product-routes', { method: 'POST', body: JSON.stringify(b) }),
  /* ⛔ Memindah rute = hapus lalu buat. Menyunting tujuannya di tempat menghapus jejak "dulu ke
     mana", dan itu pertanyaan pertama saat tiket salah ruangan. */
  deleteRoute: (id: string) =>
    req<{ deleted: boolean }>(`/master/product-routes/${id}`, { method: 'DELETE' }),

  taxGroups: (includeInactive = false) =>
    req<readonly TaxGroup[]>(
      `/master/tax-groups${qs({ includeInactive: includeInactive ? 'true' : undefined })}`,
    ),
  createTaxGroup: (b: { code: string; name: string }) =>
    req<TaxGroup>('/master/tax-groups', { method: 'POST', body: JSON.stringify(b) }),

  taxCodes: () => req<readonly TaxCode[]>('/master/tax-codes'),
  createTaxCode: (b: Record<string, unknown>) =>
    req<TaxCode>('/master/tax-codes', { method: 'POST', body: JSON.stringify(b) }),
  /**
   * ⛔ **Tarif TIDAK bisa diubah** — server hanya menerima `name` & `validTo`. Menaikkan PB1
   * dengan UPDATE mengubah cara laporan LAMA dihitung ulang, dan selisihnya muncul di SPT.
   * Cara yang benar: akhiri kode lama (`validTo`), buat kode baru.
   */
  endTaxCode: (id: string, validTo: string | null) =>
    req<TaxCode>(`/master/tax-codes/${id}`, {
      method: 'PATCH', body: JSON.stringify({ validTo }),
    }),

  outletCapabilities: (outletId?: string) =>
    req<readonly OutletCapabilityRow[]>(
      `/master/outlet-capabilities${qs({ outlet_id: outletId })}`,
    ),
  addCapability: (b: { outletId: string; capability: string }) =>
    req<OutletCapabilityRow>('/master/outlet-capabilities', {
      method: 'POST', body: JSON.stringify(b),
    }),
  removeCapability: (id: string) =>
    req<{ deleted: boolean }>(`/master/outlet-capabilities/${id}`, { method: 'DELETE' }),

  outletTaxes: (taxCodeId?: string, outletId?: string) =>
    req<readonly OutletTax[]>(
      `/master/outlet-taxes${qs({ tax_code_id: taxCodeId, outlet_id: outletId })}`,
    ),
  assignOutletTax: (b: { outletId: string; taxCodeId: string; validFrom: string }) =>
    req<OutletTax>('/master/outlet-taxes', { method: 'POST', body: JSON.stringify(b) }),
  unassignOutletTax: (id: string) =>
    req<{ deleted: boolean }>(`/master/outlet-taxes/${id}`, { method: 'DELETE' }),

  taxGroupLinks: (taxGroupId?: string) =>
    req<readonly TaxGroupLink[]>(`/master/tax-group-tax-codes${qs({ tax_group_id: taxGroupId })}`),
  linkTax: (b: { taxGroupId: string; taxCodeId: string }) =>
    req<TaxGroupLink>('/master/tax-group-tax-codes', { method: 'POST', body: JSON.stringify(b) }),
  unlinkTax: (id: string) =>
    req<{ deleted: boolean }>(`/master/tax-group-tax-codes/${id}`, { method: 'DELETE' }),

  taxChannels: (taxCodeId?: string) =>
    req<readonly TaxCodeChannel[]>(`/master/tax-code-channels${qs({ tax_code_id: taxCodeId })}`),
  restrictChannel: (b: { taxCodeId: string; channel: string }) =>
    req<TaxCodeChannel>('/master/tax-code-channels', { method: 'POST', body: JSON.stringify(b) }),
  unrestrictChannel: (id: string) =>
    req<{ deleted: boolean }>(`/master/tax-code-channels/${id}`, { method: 'DELETE' }),

  /**
   * ⭐ **Grup harga** — "tarif mal" vs "tarif ruko" tanpa menyalin harga per outlet.
   * ⛔ Soal `includeInactive`: lihat catatan di `departments`.
   */
  priceGroups: (brandId?: string, includeInactive = false) =>
    req<readonly PriceGroup[]>(
      `/master/price-groups${qs({
        brand_id: brandId,
        includeInactive: includeInactive ? 'true' : undefined,
      })}`,
    ),
  /** ⛔ Milik tenant sendiri — tenant-nya dari TOKEN, tak pernah dari parameter (R-AI-1). */
  subscription: () => req<Subscription>('/subscription'),

  /** ⛔ Tanpa satu pun metode bayar, outlet TAK BISA berjualan (gerbang kesiapan). */
  paymentMethods: (includeInactive = false) =>
    req<readonly PaymentMethod[]>(
      `/master/payment-methods${qs({ includeInactive: includeInactive ? 'true' : undefined })}`,
    ),

  /**
   * Membuat master apa pun lewat registry.
   *
   * ⚠️ Sengaja generik: tiap layar baru yang menambah pembungkus khususnya sendiri membuat berkas
   * ini tumbuh tanpa menambah kejelasan — yang membedakan sudah ada di `path`-nya.
   */
  /**
   * Membaca master apa pun lewat registry.
   *
   * ⚠️ Generik dengan sengaja: layar staf/peran menyentuh EMPAT entitas (`staff`, `roles`,
   * `role-permissions`, `position-roles`), dan menambah pembungkus khusus untuk tiap-tiapnya
   * membuat berkas ini tumbuh tanpa menambah kejelasan.
   */
  listMaster: <T>(path: string, params: Record<string, string | undefined> = {}) =>
    req<readonly T[]>(`/master/${path}${qs(params)}`),

  /**
   * ⛔ Menghapus baris master. Dipakai HANYA untuk tabel kaitan (izin peran, pemetaan jabatan) —
   * di sana "ada atau tidak" memang satu-satunya keadaan, dan mencabutnya bukan penghapusan
   * riwayat. ⚠️ Master ber-riwayat dinonaktifkan (`setStatus`), tak pernah dihapus.
   */
  deleteMaster: (path: string, id: string) =>
    req<{ id: string }>(`/master/${path}/${id}`, { method: 'DELETE' }),

  /**
   * ⭐ Memasang PIN staf. ⛔ PIN tak pernah dibaca kembali — yang tersimpan hash-nya; layar cuma
   * bisa MENGGANTI, tak bisa menampilkan.
   */
  setStaffPin: (staffId: string, pin: string) =>
    req<{ id: string }>(`/master/staff/${staffId}/pin`, {
      method: 'POST', body: JSON.stringify({ pin }),
    }),

  createMaster: (path: string, body: Record<string, unknown>) =>
    req<{ id: string }>(`/master/${path}`, { method: 'POST', body: JSON.stringify(body) }),

  createPriceGroup: (b: { brandId: string; code: string; name: string }) =>
    req<PriceGroup>('/master/price-groups', { method: 'POST', body: JSON.stringify(b) }),

  /** ⛔ Soal `includeInactive`: lihat catatan di `departments`. */
  promos: (brandId?: string, includeInactive = false) =>
    req<readonly Promo[]>(
      `/master/promos${qs({
        brand_id: brandId,
        includeInactive: includeInactive ? 'true' : undefined,
      })}`,
    ),
  createPromo: (b: Record<string, unknown>) =>
    req<Promo>('/master/promos', { method: 'POST', body: JSON.stringify(b) }),

  promoBlocks: (outletId: string) =>
    req<readonly PromoBlock[]>(`/master/outlet-promo-blocks${qs({ outlet_id: outletId })}`),
  blockPromo: (b: { outletId: string; promoId: string }) =>
    req<PromoBlock>('/master/outlet-promo-blocks', { method: 'POST', body: JSON.stringify(b) }),
  unblockPromo: (id: string) =>
    req<{ deleted: boolean }>(`/master/outlet-promo-blocks/${id}`, { method: 'DELETE' }),

  /** ⭐ Mengubah mode jual — inilah setelan yang menentukan BENTUK layar kasir. */
  setSaleMode: (id: string, saleMode: string) =>
    req<{ id: string }>(`/master/outlets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ saleMode }),
    }),
};
